/**
 * Select — styled native <select> (theme T5a §4).
 *
 * Native is deliberate: accessibility, keyboard handling, and mobile
 * pickers come for free.  For multi-select or search-as-you-type, use a
 * custom combobox popover (ships later when actually needed).
 */
import { ChevronDown } from 'lucide-react';
import type { SelectHTMLAttributes } from 'react';
import { forwardRef } from 'react';

import { cn } from './cn';

export type SelectSize = 'sm' | 'md';

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  selectSize?: SelectSize;
}

const SIZE_CLASSES: Record<SelectSize, string> = {
  sm: 'h-8 text-sm',
  md: 'h-10 text-base',
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { selectSize = 'md', className, disabled, children, ...rest },
  ref,
) {
  const invalid = rest['aria-invalid'] === true || rest['aria-invalid'] === 'true';
  return (
    <div
      className={cn(
        'relative flex w-full rounded-md border bg-white transition-colors duration-fast ease-standard',
        'border-slate-300 hover:border-slate-400',
        'focus-within:ring-brand-primary-400/50 focus-within:border-brand-primary-500 focus-within:ring-2',
        invalid && 'focus-within:ring-error-600/50 border-error-600 focus-within:border-error-600',
        disabled && 'cursor-not-allowed bg-slate-50 opacity-disabled',
        className,
      )}
    >
      <select
        ref={ref}
        className={cn(
          'w-full appearance-none bg-transparent px-3 pr-10 text-slate-800 focus:outline-none',
          SIZE_CLASSES[selectSize],
        )}
        disabled={disabled}
        {...rest}
      >
        {children}
      </select>
      <ChevronDown
        aria-hidden
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
        size={14}
        strokeWidth={1.75}
      />
    </div>
  );
});
